import React, { useEffect, useRef, useState, useContext, useMemo } from 'react';
import SvgIcon from '../../../Components/Common/Component/SvgIcon';
import { Link } from 'react-router-dom';
import { Modal, ModalBody, ModalHeader, ModalFooter, Button } from 'reactstrap';
import NotificationContext from '../../../_helper/Notification';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { H5, P } from '../../../AbstractElements';

const timeAgo = (dateString) => {
  if (!dateString) return '';
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: tr });
  } catch {
    return '';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const borderColors = ['b-l-primary', 'b-l-success', 'b-l-info', 'b-l-warning'];
const fontColors = ['font-primary', 'font-success', 'font-info', 'font-warning'];

const Notificationbar = () => {
  const [notificationDropDown, setNotificationDropDown] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isOtherLocked, setIsOtherLocked] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const dropdownRef = useRef(null);
  const {
    allNotifications,
    unreadCount,
    loading,
    getNotifications,
    GetNotificationById,
  } = useContext(NotificationContext);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) {
        setNotificationDropDown(false);
        setIsLocked(false);
        setIsOtherLocked(false);
        window.dispatchEvent(
          new CustomEvent('headerDropdownState', { detail: { source: 'notification', locked: false } })
        );
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const handleDropdownState = (event) => {
      const { source, locked } = event.detail || {};
      if (source === 'notification') return;

      if (locked) {
        setIsOtherLocked(true);
        setNotificationDropDown(false);
        setIsLocked(false);
      } else {
        setIsOtherLocked(false);
        if (!isLocked) {
          setNotificationDropDown(false);
        }
      }
    };

    window.addEventListener('headerDropdownState', handleDropdownState);

    return () => {
      window.removeEventListener('headerDropdownState', handleDropdownState);
    };
  }, [isLocked]);

  const handleDropdownToggle = () => {
    setIsLocked((prevLocked) => {
      const nextLocked = !prevLocked;
      if (nextLocked) {
        setNotificationDropDown(true);
        window.dispatchEvent(
          new CustomEvent('headerDropdownState', { detail: { source: 'notification', locked: true } })
        );
      } else {
        setNotificationDropDown(false);
        window.dispatchEvent(
          new CustomEvent('headerDropdownState', { detail: { source: 'notification', locked: false } })
        );
      }
      return nextLocked;
    });
  };

  const handleDropdownOpen = () => {
    if (isLocked || isOtherLocked) return;
    setNotificationDropDown(true);
    window.dispatchEvent(
      new CustomEvent('headerDropdownState', { detail: { source: 'notification', locked: false } })
    );
  };

  const handleDropdownClose = () => {
    if (isLocked) return;
    setNotificationDropDown(false);
    window.dispatchEvent(
      new CustomEvent('headerDropdownState', { detail: { source: 'notification', locked: false } })
    );
  };

  const handleNotificationClick = async (id) => {
    setDetailLoading(true);
    try {
      const notification = await GetNotificationById(id);
      if (notification) {
        setSelectedNotification(notification);
        setModalOpen(true);
      }
    } catch {
      // Hata sessizce yutulur
    } finally {
      setDetailLoading(false);
      setNotificationDropDown(false);
      setIsLocked(false);
      setIsOtherLocked(false);
      window.dispatchEvent(
        new CustomEvent('headerDropdownState', { detail: { source: 'notification', locked: false } })
      );
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedNotification(null);
    getNotifications();
  };

  // Okunmamış bildirimlerden en güncel 4 tanesini listeler
  const latestNotifications = useMemo(() => {
    return [...allNotifications]
      .filter(notification => !notification.isRead)
      .sort((a, b) => new Date(b.created) - new Date(a.created))
      .slice(0, 4);
  }, [allNotifications]);

  return (
    <li
      className='onhover-dropdown'
      onMouseEnter={handleDropdownOpen}
      onMouseLeave={handleDropdownClose}
      ref={dropdownRef}
    >
      <div className='notification-box' onClick={handleDropdownToggle}>
        <SvgIcon iconId='notification' />
        {unreadCount > 0 && (
          <span className='badge rounded-pill badge-secondary'>{unreadCount}</span>
        )}
      </div>
      <div
        className={`notification-dropdown onhover-show-div ${notificationDropDown ? 'active' : ''}`}
        style={{ cursor: 'default' }}
      >
        <h6 className='f-18 mb-0 dropdown-title'>Bildirimler ({unreadCount})</h6>
        {loading ? (
          <div className="p-3 text-center text-muted">Yükleniyor...</div>
        ) : (
          <>
            {latestNotifications.length > 0 ? (
              <ul>
                {latestNotifications.map((notification, index) => (
                  <li
                    key={notification.id}
                    className={`${borderColors[index % borderColors.length]} border-4`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleNotificationClick(notification.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        handleNotificationClick(notification.id);
                      }
                    }}
                  >
                    <p className="mb-0">
                      {notification.title || notification.message}
                      <span className={`${fontColors[index % fontColors.length]}`}>{' '}{timeAgo(notification.created)}</span>
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-3 text-center text-muted">Yeni bildirim bulunmamaktadır.</div>
            )}
            <ul className="notification-footer">
              <li className="text-center">
                <Link
                  className='f-w-700'
                  to={`${process.env.PUBLIC_URL}/notifications`}
                  onClick={() => {
                    setNotificationDropDown(false);
                    setIsLocked(false);
                    setIsOtherLocked(false);
                    window.dispatchEvent(
                      new CustomEvent('headerDropdownState', { detail: { source: 'notification', locked: false } })
                    );
                  }}
                >
                  Tümünü Göster
                </Link>
              </li>
            </ul>
          </>
        )}
      </div>
      <Modal isOpen={modalOpen} toggle={closeModal} size="lg" backdrop="static">
        <ModalHeader toggle={closeModal} className="d-flex justify-content-between align-items-start">
          {selectedNotification && formatDate(selectedNotification.created) && (
            <span className="text-muted small">{formatDate(selectedNotification.created)}</span>
          )}
        </ModalHeader>
        <ModalBody>
          {selectedNotification ? (
            <div>
              <H5 className="modal-title me-auto mb-3">{selectedNotification.title}</H5>
              <P style={{ whiteSpace: 'pre-wrap' }}>
                {selectedNotification.message}
              </P>

              {selectedNotification.details && (
                <div className="mt-3">
                  <P style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedNotification.details}
                  </P>
                </div>
              )}

              {selectedNotification.priority && selectedNotification.priority !== 'normal' && (
                <div className="mt-3">
                  <span className={`badge ${
                    selectedNotification.priority === 'high' ? 'badge-light-danger' :
                    selectedNotification.priority === 'medium' ? 'badge-light-warning' :
                    'badge-light-info'
                  }`}>
                    {selectedNotification.priority === 'high' ? 'Yüksek Öncelik' :
                     selectedNotification.priority === 'medium' ? 'Orta Öncelik' :
                     selectedNotification.priority}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center p-4">
              <span className="text-muted">
                {detailLoading ? 'Bildirim detayları yükleniyor...' : 'Bildirim bulunamadı.'}
              </span>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="outline-primary" onClick={closeModal}>
            Kapat
          </Button>
        </ModalFooter>
      </Modal>
    </li>
  );
};

export default Notificationbar;
